import { useState } from 'react';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import CopyToClipboardIcon from '@cp/src/assets/icons/CopyToClipboardIcon';
import { ClickTopCopy } from '@cp/src/utils/commonUtils';
import InfoIconOutlined from '@cp/src/assets/images/icons/InfoIconOutlined';
import { Content, PAYMENT_MODE } from '../../interfaces/RepaymentHistory.type';
import styles from './index.module.scss';
import cx from 'classnames';
import { convertToTitleCase } from './utils';
import { PLACEHOLDER_DASH, PAYMENT_GATEWAY } from './const';

interface IAdditionalDetails {
  rowData: Content;
}

const AdditionalDetails = (props: IAdditionalDetails) => {
  const [textOnTooltip, setTextOnTooltip] = useState('Click to copy');
  const [arrowColor, setArrowColor] = useState('var(--grayscale-warm-4)');
  const handleCopyToClipboard = (text: string) => {
    ClickTopCopy({ text: text, setArrowColor: setArrowColor, setTooltipContent: setTextOnTooltip });
  };

  const { rowData } = props;
  if (!rowData) return null;
  const { repaymentMetadata } = rowData;

  return (
    <div className="flex flex-row items-center h-20 p-4 bg-[--blue-background-primary]">
      <div className="flex justify-around">
        <div className="flex flex-col gap-2 pl-2 pr-3">
          <div className="flex flex-row">
            <Typography variant="h6" color="var(--navi-color-gray-c2)">
              Order ID
            </Typography>
            {repaymentMetadata?.orderReferenceId ? (
              <Tooltip placement="top">
                <TooltipTrigger className="tooltipContent">
                  <Button
                    variant="text"
                    onClick={() => handleCopyToClipboard(repaymentMetadata?.orderReferenceId)}
                    className="ml-1"
                  >
                    <CopyToClipboardIcon width={16} height={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="tooltipWrapper">{textOnTooltip}</TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <Typography
            variant="p3"
            className="flex items-center mr-2"
            color="var(--navi-color-gray-c3)"
          >
            {repaymentMetadata?.orderReferenceId || PLACEHOLDER_DASH}
          </Typography>
        </div>

        <div className="flex flex-col gap-2 pl-3 pr-3">
          <Typography variant="h6" color="var(--navi-color-gray-c2)">
            Provider
          </Typography>
          <Typography variant="p3" color="var(--navi-color-gray-c3)">
            {repaymentMetadata?.provider || PLACEHOLDER_DASH}
          </Typography>
        </div>
        {rowData?.status === 'FAILURE' ? (
          <div className="flex flex-col gap-2 pl-3 pr-3">
            <Typography variant="h6" color="var(--navi-color-gray-c2)">
              Failure reason
            </Typography>
            <div className={cx('flex flex-row', styles.capitaliseFirstLetter)}>
              <Typography variant="p3" color="var(--navi-color-gray-c3)">
                {convertToTitleCase(repaymentMetadata?.errorCode) || PLACEHOLDER_DASH}
              </Typography>
              {repaymentMetadata?.errorDescription ? (
                <div className="mt-0.5 ml-1 z-40">
                  <Tooltip placement="top">
                    <TooltipTrigger className="tooltipContent">
                      <Button variant="text">
                        <InfoIconOutlined fillColor={'var(--blue-base)'} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="tooltipWrapper">
                      {convertToTitleCase(repaymentMetadata?.errorDescription)}
                    </TooltipContent>
                  </Tooltip>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div
        className={cx({
          'flex flex-col ml-8 mr-8 h-12 w-auto': true,
          [styles.leftBorder]: rowData?.repaymentMode !== PAYMENT_GATEWAY
        })}
      ></div>

      {rowData?.repaymentMode !== PAYMENT_GATEWAY ? (
        <div className="flex justify-around">
          <div className="flex flex-col gap-2 pl-2 pr-3">
            <Typography variant="h6" color="var(--navi-color-gray-c2)">
              Bank IFSC
            </Typography>
            <Typography variant="p3" color="var(--navi-color-gray-c3)">
              {repaymentMetadata?.bankIfsc || PLACEHOLDER_DASH}
            </Typography>
          </div>

          <div className="flex flex-col gap-2 pl-3 pr-2">
            <Typography variant="h6" color="var(--navi-color-gray-c2)">
              Account Number
            </Typography>
            <Typography variant="p3" color="var(--navi-color-gray-c3)">
              {repaymentMetadata.bankAccountNumber || PLACEHOLDER_DASH}
            </Typography>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdditionalDetails;
